import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  noindex?: boolean;
}

export const useSEO = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage = '/og-image.png',
  ogUrl,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonical,
  noindex = false
}: SEOProps) => {
  useEffect(() => {
    // Set page title
    document.title = title;

    // Helper function to set or create meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
    };

    // Helper function to set or create link tag
    const setLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (link) {
        link.setAttribute('href', href);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', href);
        document.getElementsByTagName('head')[0].appendChild(link);
      }
    };

    // Basic meta tags
    setMetaTag('description', description);
    
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content')?.includes('noindex')) {
        robotsMeta.remove();
      }
    }

    // Open Graph tags
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:site_name', 'DocketLabs', true);
    
    if (ogUrl) {
      setMetaTag('og:url', ogUrl, true);
    }

    // Twitter Card tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', twitterTitle || ogTitle || title);
    setMetaTag('twitter:description', twitterDescription || ogDescription || description);
    setMetaTag('twitter:image', twitterImage || ogImage);
    setMetaTag('twitter:site', '@DocketLabs');

    // Canonical URL
    if (canonical) {
      setLinkTag('canonical', canonical);
    }

    // Cleanup function to remove meta tags when component unmounts
    return () => {
      // Note: We don't actually remove meta tags on unmount as they should persist
      // until the next page sets new ones
    };
  }, [
    title, 
    description, 
    ogTitle, 
    ogDescription, 
    ogImage, 
    ogUrl, 
    twitterCard, 
    twitterTitle, 
    twitterDescription, 
    twitterImage, 
    canonical, 
    noindex
  ]);
};

export default useSEO;